import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { Bookmark } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { LineItemTable } from '@/components/LineItemTable'
import { LineItemFilters, useLineItemFilters } from '@/components/LineItemFilters'
import { SearchInput } from '@/components/SearchInput'
import { useNetwork } from '../contexts/NetworkContext'
import { useTaggedLineItems } from '@/hooks/useTaggedLineItems'
import { getContext } from '@/services/networks/networksApi'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import { filterBySearch } from '@/utils/search'
import type { LineItem } from '@/services/lineItems/lineItemsApi'

/** The line items the user has tagged, most recently tagged first. */
export function TaggedLineItemsCard() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady } = useNetwork()
  const context = getContext()
  const { ids, isLoading: idsLoading, setTagged } = useTaggedLineItems()
  const { filters, setFilters, applyFilters, defaults } = useLineItemFilters(
    { objectState: 'all' },
    'taggedLineItems'
  )
  const [search, setSearch] = useState('')

  // Starts with 'lineitems' so a save on the line item page refreshes it too.
  const { data = [], isLoading } = useQuery({
    queryKey: ['lineitems', 'tagged', ids, context, scopeKey],
    queryFn: () => LineItemsApi.getByIds(ids, { withTargeting: true }),
    enabled: scopeReady && ids.length > 0,
  })

  /**
   * Dropping a bookmark from the card itself, rather than having to open the
   * line item to reach its bookmark button.
   */
  const untagColumn = useMemo<ColumnDef<LineItem, unknown>[]>(
    () => [
      {
        id: 'untag',
        header: '',
        size: 44,
        enableSorting: false,
        enableHiding: false,
        meta: { label: t('lineItems.tag.untag') },
        cell: ({ row }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t('lineItems.tag.untag')}
                // The row itself opens the line item.
                onClick={async (event) => {
                  event.stopPropagation()
                  try {
                    await setTagged(row.original.id, false)
                  } catch (e) {
                    toast.error(t('lineItems.tag.failed'), { description: (e as Error).message })
                  }
                }}
              >
                <Bookmark className="size-4 fill-current text-(--ad-text-muted)" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('lineItems.tag.untag')}</TooltipContent>
          </Tooltip>
        ),
      },
    ],
    [t, setTagged]
  )

  // The API returns them in its own order; keep the order they were tagged in.
  const rows = useMemo(() => {
    const byId = new Map(data.map((li) => [li.id, li]))
    return ids.flatMap((id) => byId.get(id) ?? [])
  }, [data, ids])

  return (
    <ListCard>
      <ListCardHeader
        title={
          <>
            <CardIcon icon={Bookmark} />
            {t('advertisingHome.tagged')}
          </>
        }
        count={rows.length}
        controls={
          <>
            <SearchInput value={search} onChange={setSearch} />
            <LineItemFilters filters={filters} onChange={setFilters} defaults={defaults} />
          </>
        }
      />
      <CardContent className="p-0">
        <LineItemTable
          lineItems={filterBySearch(applyFilters(rows), search)}
          loading={idsLoading || (ids.length > 0 && isLoading)}
          emptyMessage={ids.length === 0 ? t('advertisingHome.taggedEmpty') : undefined}
          leadingColumns={untagColumn}
        />
      </CardContent>
    </ListCard>
  )
}
